import React, { useState, useEffect, useRef } from 'react'
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import ApiEndpoints, {BASE_URL, BASE_URL_Upload} from './commonComponents/ApiEndpoints'

import {
  FaCalendarDay,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaCreditCard,
  FaWhatsapp,
  FaCheck,
} from 'react-icons/fa'
import {
  BsArrowClockwise,
  BsFileEarmarkSpreadsheetFill,
  BsFileEarmarkPdfFill,
  BsFunnelFill,
  BsSearch,
  BsCalendar3,
} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js'
import Cookies from 'js-cookie'
import axios from 'axios'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import {
  NO_IMAGE_SRC,
  resolveInstituteLogoSrc,
} from '../utils/instituteBranding'
import {
  canvasToShareImage,
  isReactNativeWebView,
  nativeShareErrorMessage,
  openWhatsApp,
  requestNativeImageShare,
  shareFilesWithWebShare,
} from '../utils/nativeImageShare'
import moment from 'moment'
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend)

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/** A stalled logo request must not hold the whole receipt capture hostage. */
const LOGO_FETCH_TIMEOUT_MS = 6000

function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), LOGO_FETCH_TIMEOUT_MS)
  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timer),
  )
}

/** Inline remote logos so html2canvas can paint them without CORS blanks. */
async function fetchImageAsDataUrlForCanvas(url) {
  if (!url || url.startsWith('data:')) return url
  let absolute = url
  try {
    absolute = new URL(url, window.location.href).href
  } catch {
    /* use url as-is */
  }
  try {
    const res = await fetchWithTimeout(absolute, {
      mode: 'cors',
      credentials: 'omit',
    })
    if (res.ok) return await blobToDataURL(await res.blob())
  } catch {
    /* cross-origin without CORS */
  }
  try {
    const proxied = `https://images.weserv.nl/?url=${encodeURIComponent(absolute)}`
    const res = await fetchWithTimeout(proxied)
    if (!res.ok) throw new Error(String(res.status))
    return await blobToDataURL(await res.blob())
  } catch {
    /* try second proxy */
  }
  try {
    const res = await fetchWithTimeout(
      `https://api.allorigins.win/raw?url=${encodeURIComponent(absolute)}`,
    )
    if (!res.ok) throw new Error(String(res.status))
    return await blobToDataURL(await res.blob())
  } catch (e) {
    console.warn('WhatsApp receipt: could not inline logo', absolute, e)
    return absolute
  }
}

async function inlineImagesForCapture(root) {
  const imgs = [...root.querySelectorAll('img')]
  for (const img of imgs) {
    const current = img.currentSrc || img.src
    if (!current || current.startsWith('data:')) continue
    try {
      const dataUrl = await fetchImageAsDataUrlForCanvas(current)
      if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
        img.src = dataUrl
        if (img.decode) await img.decode().catch(() => {})
      }
    } catch {
      /* leave original src */
    }
  }
}

export default function FeeCollection() {
  const { user, logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name, id } = useInstitute();
  const instituteDisplayName = String(
    name || Cookies.get('name') || Cookies.get('institute_name') || '',
  ).trim()
  const instituteDisplayLogo =
    logo && logo !== NO_IMAGE_SRC
      ? logo
      : resolveInstituteLogoSrc(Cookies.get('institute_logo') || 'no_image.png')
  const [showFilterCard, setShowFilterCard] = useState(false)
  
  const [data, setData] = useState([])
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [dataByDate, setDataByDate] = useState([])
  const [noDataMessage, setNoDataMessage] = useState('')
  const [paymentFilter, setPaymentFilter] = useState('all')
  const [allFromData, setAllFromData] = useState('')

  const [title, setTitle] = useState(
    `Today's Collection (${moment().format('DD/MM/YYYY')})`,
  )
  const [receiptItem, setReceiptItem] = useState(null)
  const [sharingFeeId, setSharingFeeId] = useState(null)
  const receiptCardRef = useRef(null)
  const receiptReadyResolverRef = useRef(null)

  useEffect(() => {
    if (receiptItem && receiptCardRef.current && receiptReadyResolverRef.current) {
      const resolve = receiptReadyResolverRef.current
      receiptReadyResolverRef.current = null
      resolve()
    }
  }, [receiptItem])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const instituteId = Cookies.get('institute_id')
        console.log('INSTITUTE ID: ', instituteId)
        const headers = {
          Authorization: `Bearer ${user.token}`,
          'institute-Id': Number(instituteId),
        }
        console.log('HEADERS: ', headers)
        const response = await axios.get(
          BASE_URL+'/fee-collection',
          { headers },
        )
        console.log('Response of data: ', response)
        setData(response.data.data)
      } catch (error) {
        console.log(error.response?.data?.message)
      }
    }
    fetchData()
  }, [])

  const fetchDatabyDate = async (from = '', to = '', paymentMethod = 'all') => {
    try {
      const instituteId = Cookies.get('institute_id')
      const headers = {
        Authorization: `Bearer ${user.token}`,
        'institute-Id': Number(instituteId),
      }

      const params = new URLSearchParams()
      if (from) params.append('from_date', from)
      if (to) params.append('to_date', to)
      if (paymentMethod && paymentMethod !== 'all') {
        params.append('payment_method', paymentMethod)
      }

      let url =
        BASE_URL+'/get/fee-collection/by-date'
      if (params.toString()) url += '?' + params.toString()

      const response = await axios.get(url, { headers })
      console.log('RESPONSE during selected fees:', response.data.data)
      setDataByDate(response.data.data)
    } catch (error) {
      console.log(error.response?.data?.message)
      setDataByDate([])
      setNoDataMessage(error.response?.data?.message)
    }
  }

  useEffect(() => {
    fetchDatabyDate()
  }, [])

  const handleDateChange = (type, value) => {
    console.log('Change triggered:', { type, value })
    if (type === 'from') {
      console.log('From date is set: ', value)
      setFromDate(value)
    } else {
      setToDate(value)
      console.log('To date is set:', value)
    }

    const newFrom = type === 'from' ? value : fromDate
    const newTo = type === 'to' ? value : toDate

    console.log('Evaluted dates:', { newFrom, toDate })

    if (newFrom && newTo) {
      const displayTitle =
        newFrom === newTo
          ? `${moment(newFrom).format('D MMM YYYY')} Collection`
          : `${moment(newFrom).format('D MMM YYYY')} - ${moment(newTo).format(
              'D MMM YYYY',
            )} Collection`

      console.log('Tittle (both dates):', displayTitle)
      setTitle(displayTitle)
      fetchDatabyDate(newFrom, newTo, paymentFilter)
    }
    // else if (newFrom) {
    //   const title = `${moment(newFrom).format('D MMM YYYY')} Collection`
    //   console.log('Title (from date only):', title)
    //   setTitle(title)
    //   fetchDatabyDate(newFrom, '', paymentFilter)
    // } else if (newTo) {
    //   setTitle(`Upto ${moment(newTo).format('D MMM YYYY')} Collection`)
    //   fetchDatabyDate('', newTo, paymentFilter)
    // }
  }

  useEffect(() => {
    fetchDatabyDate(fromDate, toDate, paymentFilter)
  }, [paymentFilter])

  const handleFromDateChange = (type, value) => {
    if (type === 'from') {
      setFromDate(value)

      if (value) {
        const title = `${moment(value).format('D MMM YYYY')} Collection`
        console.log('Title (from date only):', title)
        setTitle(title)

        // Call API with selected_date as query param
        fetchFromDate(value, paymentFilter)
      }
    }
  }

  const fetchFromDate = async (from = '', paymentMethod = 'all') => {
    try {
      const instituteId = Cookies.get('institute_id')
      const headers = {
        Authorization: `Bearer ${user.token}`,
        'institute-Id': Number(instituteId),
      }

      const params = new URLSearchParams()
      if (from) params.append('selected_date', from)
      if (paymentMethod && paymentMethod !== 'all') {
        params.append('payment_method', paymentMethod)
      }

      let url =
        BASE_URL+'/get/fee-collection/by-date'
      if (params.toString()) url += '?' + params.toString()

      console.log('Request URL:', url) // ✅ Check the correct URL

      const response = await axios.get(url, { headers })
      setDataByDate(response.data.data)
      setNoDataMessage('')
    } catch (error) {
      setDataByDate([])
      setNoDataMessage(error.response?.data?.message || 'No data found')
    }
  }

  useEffect(() => {
    fetchFromDate(fromDate)
  }, [])

  const handleRefresh = () => {
    setFromDate('')
    setToDate('')
    setPaymentFilter('all')
    setTitle(`Today's Collection (${moment().format('DD/MM/YYYY')})`)
    fetchDatabyDate()
  }

  const getReceiptNo = (item) => {
    return `${new Date(item.transaction_date).getFullYear()}${String(
      new Date(item.transaction_date).getMonth() + 1,
    ).padStart(2, '0')}${item.id.toString(16).toUpperCase()}`
  }

  const getFilteredTransactions = () => {
    return dataByDate.filter(
      (item) =>
        paymentFilter === 'all' ||
        (item.payment_method &&
          item.payment_method.toLowerCase() === paymentFilter),
    )
  }

  const toAmountNumber = (value) => {
    const num = Number(value)
    return Number.isFinite(num) ? num : 0
  }

  const formatINR = (value) => {
    if (value === null || value === undefined || value === '') return ''
    const num = Number(value)
    if (Number.isNaN(num)) return ''
    return `₹ ${num}`
  }

  const formatINRLocale = (value) =>
    `₹${toAmountNumber(value).toLocaleString('en-IN')}`

  const getOnlineAmount = (item) => {
    const method = (item?.payment_method || '').toLowerCase()
    if (method === 'split') return item?.online
    if (method === 'online') return item?.transaction_amount
    return ''
  }

  const getCashAmount = (item) => {
    const method = (item?.payment_method || '').toLowerCase()
    if (method === 'split') return item?.cash
    if (method === 'cash') return item?.transaction_amount
    return ''
  }

  const getChequeAmount = (item) => {
    const method = (item?.payment_method || '').toLowerCase()
    if (method === 'split') return item?.cheque
    if (method === 'cheque' || method === 'check') return item?.transaction_amount
    return ''
  }

  const getCollectionTotals = (transactions = []) =>
    transactions.reduce(
      (acc, item) => {
        acc.totalAmount += toAmountNumber(item?.transaction_amount)
        acc.totalDiscount += toAmountNumber(item?.discount)
        acc.totalOnline += toAmountNumber(getOnlineAmount(item))
        acc.totalCash += toAmountNumber(getCashAmount(item))
        acc.totalCheque += toAmountNumber(getChequeAmount(item))
        return acc
      },
      {
        totalAmount: 0,
        totalDiscount: 0,
        totalOnline: 0,
        totalCash: 0,
        totalCheque: 0,
      },
    )

  const isNullOrEmpty = (value) =>
    value === null || value === undefined || value === ''

  const mobileEmptyClass = (value) =>
    isNullOrEmpty(value) ? 'fee-collection-hide-empty-mobile' : ''

  const waitForPaint = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    })

  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const captureReceiptImage = async (item) => {
    const ready = new Promise((resolve, reject) => {
      receiptReadyResolverRef.current = resolve
      setTimeout(() => {
        if (receiptReadyResolverRef.current) {
          receiptReadyResolverRef.current = null
          reject(new Error('Receipt preview timed out'))
        }
      }, 4000)
    })
    setReceiptItem(item)
    await ready
    await waitForPaint()

    const node = receiptCardRef.current
    if (!node) throw new Error('Receipt preview not ready')

    await inlineImagesForCapture(node)
    await waitForPaint()

    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#223843',
      logging: false,
    })

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => (result ? resolve(result) : reject(new Error('Image failed'))),
        'image/png',
      )
    })

    const receiptNo = getReceiptNo(item)
    const fileName = `Fee_Receipt_${receiptNo}.png`
    const file = new File([blob], fileName, { type: 'image/png' })
    return { blob, file, fileName, receiptNo, canvas }
  }

  /**
   * Receipt text in the same shape PendingFee uses — WhatsApp opens immediately
   * on the tap, which is what makes sharing work inside the React Native app.
   */
  const buildReceiptMessage = (item) => {
    const studentName = item.student_name || 'Student'
    const instituteLabel = instituteDisplayName || 'Institute'
    const amount = item.transaction_amount ?? 0
    const receiptNo = getReceiptNo(item)
    const courseName = item.course_name || ''
    const feesName = item.fees_name || 'Fee payment'
    const paymentMethod = (item.payment_method || '').toUpperCase()
    const paidOn = item.transaction_date
      ? moment(item.transaction_date).format('DD MMM YYYY')
      : ''

    const lines = [
      `Dear ${studentName},`,
      '',
      `This is your fee receipt from *${instituteLabel}*.`,
      '',
      `*Amount Paid:* Rs. ${amount}`,
      `*Receipt No:* ${receiptNo}`,
      feesName ? `*Fee:* ${feesName}` : '',
      courseName ? `*Course:* ${courseName}` : '',
      item.admission_number
        ? `*Admission No:* ${item.admission_number}`
        : '',
      paymentMethod ? `*Payment Mode:* ${paymentMethod}` : '',
      item.transaction_id
        ? `*Transaction No:* ${item.transaction_id}`
        : '',
      paidOn ? `*Date:* ${paidOn}` : '',
      '',
      'Thank you.',
    ].filter(Boolean)

    return lines.join('\n')
  }

  const shareReceiptOnWhatsApp = async (item) => {
    if (!item?.id) {
      toast.error('Fee record not found')
      return
    }
    if (sharingFeeId) return

    setSharingFeeId(item.id)
    const toastId = toast.loading('Preparing receipt image...')

    try {
      const { blob, file, fileName, receiptNo, canvas } =
        await captureReceiptImage(item)

      // App WebView: share the receipt IMAGE via the native share sheet
      // (postMessage → RN → react-native-share). Plain wa.me links cannot
      // attach files — only this bridge can put the PNG into a WhatsApp chat.
      if (isReactNativeWebView()) {
        // Some newer WebViews expose Web Share; try that first.
        const webShareInApp = await shareFilesWithWebShare([file])
        if (webShareInApp === 'shared') {
          toast.success('Receipt image ready — choose WhatsApp to send.', {
            id: toastId,
          })
          return
        }
        if (webShareInApp === 'cancelled') {
          toast('Share cancelled.', { id: toastId })
          return
        }

        const result = await requestNativeImageShare([
          canvasToShareImage(canvas, `Fee_Receipt_${receiptNo}`, {
            preferJpeg: true,
          }),
        ])
        if (result.status === 'shared') {
          toast.success('Receipt image ready — choose the WhatsApp chat.', {
            id: toastId,
          })
          return
        }
        if (result.status === 'cancelled') {
          toast('Share cancelled.', { id: toastId })
          return
        }
        throw new Error(nativeShareErrorMessage(result))
      }

      // Browser: share the branded receipt image.
      const webShare = await shareFilesWithWebShare([file])
      if (webShare === 'shared') {
        toast.success('Receipt image ready — choose WhatsApp to send.', {
          id: toastId,
        })
        return
      }
      if (webShare === 'cancelled') {
        downloadBlob(blob, fileName)
        toast('Share cancelled. Receipt image downloaded.', { id: toastId })
        return
      }

      downloadBlob(blob, fileName)
      openWhatsApp('', buildReceiptMessage(item))
      toast.success(
        'Receipt image downloaded. WhatsApp opened — attach the image and pick the contact.',
        { id: toastId },
      )
    } catch (error) {
      console.error(error)
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to prepare WhatsApp receipt',
        { id: toastId },
      )
    } finally {
      setSharingFeeId(null)
      setReceiptItem(null)
    }
  }

  const exportToExcel = () => {
    const filteredTransactions = getFilteredTransactions()
    if (filteredTransactions.length === 0) {
      toast.error('No records to export')
      return
    }

    const totals = getCollectionTotals(filteredTransactions)
    const exportData = filteredTransactions.map((data) => {
      return {
        'Fees Receipt No.': getReceiptNo(data),
        'Course Name': data.course_name || '',
        'Admission Number': data.admission_number,
        Name: data.student_name,
        'Fees Name': data.fees_name,
        'Transaction Amount': toAmountNumber(data.transaction_amount),
        Discount: toAmountNumber(data.discount),
        'Transaction Date': data.transaction_date,
        'Payment Method': data.payment_method,
        Online: toAmountNumber(getOnlineAmount(data)),
        Cash: toAmountNumber(getCashAmount(data)),
        Cheque: toAmountNumber(getChequeAmount(data)),
        'Transaction Number': data.transaction_id || '',
        'Fees Taken By': data.taken_by_name || '',
      }
    })

    exportData.push({
      'Fees Receipt No.': '',
      'Course Name': '',
      'Admission Number': '',
      Name: '',
      'Fees Name': '',
      'Transaction Amount': '',
      Discount: '',
      'Transaction Date': '',
      'Payment Method': '',
      Online: '',
      Cash: '',
      Cheque: '',
      'Transaction Number': '',
      'Fees Taken By': '',
    })

    exportData.push({
      'Fees Receipt No.': 'TOTAL',
      'Course Name': '',
      'Admission Number': '',
      Name: '',
      'Fees Name': '',
      'Transaction Amount': totals.totalAmount,
      Discount: totals.totalDiscount,
      'Transaction Date': '',
      'Payment Method': '',
      Online: totals.totalOnline,
      Cash: totals.totalCash,
      Cheque: totals.totalCheque,
      'Transaction Number': '',
      'Fees Taken By': '',
    })

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions')

    XLSX.writeFile(workbook, 'Transaction_Records.xlsx')
  }

  const handleSaveAsPDF = async () => {
    const filteredTransactions = getFilteredTransactions()
    if (filteredTransactions.length === 0) {
      toast.error('No records to export')
      return
    }

    try {
      const doc = new jsPDF('l', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 8
      const rowHeight = 8
      let yPos = 22
      const totals = getCollectionTotals(filteredTransactions)

      const headers = [
        'Receipt No.',
        'Course Name',
        'Adm. No.',
        'Student Name',
        'Fees Name',
        'Amount',
        'Discount',
        'Date',
        'Payment',
        'Online',
        'Cash',
        'Cheque',
        'Txn No.',
        'Taken By',
      ]

      const colWidths = [20, 22, 16, 24, 24, 16, 15, 18, 15, 15, 15, 15, 22, 20]

      const drawTableHeader = () => {
        let xPos = margin
        doc.setFillColor(34, 56, 67)
        doc.rect(
          margin,
          yPos,
          pageWidth - margin * 2,
          rowHeight,
          'F',
        )
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(8)
        doc.setFont('helvetica', 'bold')
        headers.forEach((header, index) => {
          doc.text(header, xPos + 1, yPos + 5.5)
          xPos += colWidths[index]
        })
        yPos += rowHeight
      }

      const fitText = (text, maxLength = 18) => {
        const safeText = String(text || '')
        if (safeText.length <= maxLength) return safeText
        return `${safeText.slice(0, maxLength - 3)}...`
      }

      const formatPdfAmount = (value) => {
        const num = toAmountNumber(value)
        return num ? `Rs ${num}` : ''
      }

      const ensureSpace = (neededHeight) => {
        if (yPos + neededHeight > pageHeight - margin) {
          doc.addPage()
          yPos = 14
          return true
        }
        return false
      }

      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(34, 56, 67)
      doc.text(instituteDisplayName || 'Institute', pageWidth / 2, 10, {
        align: 'center',
      })

      doc.setFontSize(12)
      doc.text(`Fees Collection - ${title}`, pageWidth / 2, 16, {
        align: 'center',
      })

      drawTableHeader()

      doc.setFont('helvetica', 'normal')
      doc.setTextColor(17, 24, 39)
      doc.setFontSize(7.5)

      filteredTransactions.forEach((item, index) => {
        if (ensureSpace(rowHeight + 2)) {
          drawTableHeader()
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(17, 24, 39)
          doc.setFontSize(7.5)
        }

        let xPos = margin
        const rowData = [
          getReceiptNo(item),
          fitText(item.course_name, 16),
          fitText(item.admission_number || '-', 12),
          fitText(item.student_name, 18),
          fitText(item.fees_name, 18),
          formatPdfAmount(item.transaction_amount),
          formatPdfAmount(item.discount),
          new Date(item.transaction_date).toLocaleDateString('en-GB'),
          item.payment_method?.toUpperCase() || 'N/A',
          formatPdfAmount(getOnlineAmount(item)),
          formatPdfAmount(getCashAmount(item)),
          formatPdfAmount(getChequeAmount(item)),
          fitText(item.transaction_id, 16),
          fitText(item.taken_by_name || '-', 14),
        ]

        if (index % 2 !== 0) {
          doc.setFillColor(249, 250, 251)
          doc.rect(margin, yPos, pageWidth - margin * 2, rowHeight, 'F')
        }

        rowData.forEach((cell, colIndex) => {
          doc.text(String(cell), xPos + 1, yPos + 5.5)
          xPos += colWidths[colIndex]
        })

        yPos += rowHeight
      })

      // Separate collection summary box
      yPos += 6
      const summaryHeight = 28
      ensureSpace(summaryHeight + 4)
      doc.setFillColor(34, 56, 67)
      doc.roundedRect(margin, yPos, pageWidth - margin * 2, summaryHeight, 2, 2, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text('Collection Summary', margin + 4, yPos + 7)

      doc.setFontSize(8.5)
      doc.setFont('helvetica', 'normal')
      const summaryItems = [
        `Total Amount: Rs ${totals.totalAmount.toLocaleString('en-IN')}`,
        `Total Discount: Rs ${totals.totalDiscount.toLocaleString('en-IN')}`,
        `Total Cash: Rs ${totals.totalCash.toLocaleString('en-IN')}`,
        `Total Online: Rs ${totals.totalOnline.toLocaleString('en-IN')}`,
        `Total Cheque: Rs ${totals.totalCheque.toLocaleString('en-IN')}`,
      ]
      const summaryGap = (pageWidth - margin * 2 - 8) / summaryItems.length
      summaryItems.forEach((label, index) => {
        doc.text(label, margin + 4 + index * summaryGap, yPos + 18)
      })

      doc.save('Fee_Collection_Records.pdf')
    } catch (error) {
      console.error('Error while generating PDF:', error)
      toast.error('Failed to generate PDF')
    }
  }

  const filteredTransactions = getFilteredTransactions()
  const collectionTotals = getCollectionTotals(filteredTransactions)

  const summaryCards = [
    {
      label: 'Total Amount',
      value: collectionTotals.totalAmount,
      gradient: 'linear-gradient(135deg, #223843 0%, #1a2c36 100%)',
    },
    {
      label: 'Total Cash',
      value: collectionTotals.totalCash,
      gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    },
    {
      label: 'Total Online',
      value: collectionTotals.totalOnline,
      gradient: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
    },
    {
      label: 'Total Cheque',
      value: collectionTotals.totalCheque,
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    },
    {
      label: 'Total Discount',
      value: collectionTotals.totalDiscount,
      gradient: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
    },
  ]

  return (
    <>
      <style>
        {`
          .filter-card-container {
            overflow: visible !important;
            position: relative !important;
            z-index: 10 !important;
          }

          /* Match react-super-responsive-table card breakpoint (40em) */
          @media screen and (max-width: 40em) {
            .fee-collection-table .fee-collection-hide-empty-mobile {
              display: none !important;
            }
            .fee-collection-table .fee-collection-action-col,
            .fee-collection-table td.fee-collection-action-col.pivoted {
              display: block !important;
              position: relative !important;
              padding: 12px 10px 6px 10px !important;
              margin-top: 6px;
              border: none !important;
              border-top: 1px solid #E5E7EB !important;
              text-align: left !important;
            }
            .fee-collection-table .fee-collection-action-col .tdBefore {
              display: none !important;
            }
            .fee-collection-whatsapp-btn {
              width: 100%;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              background: #25D366;
              color: #fff;
              border: none;
              border-radius: 10px;
              padding: 10px 14px;
              font-size: 14px;
              font-weight: 600;
              font-family: Inter, system-ui, sans-serif;
              cursor: pointer;
              box-shadow: 0 4px 12px rgba(37, 211, 102, 0.35);
            }
          }

          /* Desktop/tablet: keep wide table fully scrollable instead of clipped */
          @media screen and (min-width: 40.01em) {
            .fee-collection-table-wrapper {
              width: 100%;
              max-width: 100%;
              overflow-x: auto;
              overflow-y: visible;
              -webkit-overflow-scrolling: touch;
            }
            .fee-collection-table-wrapper .fee-collection-table,
            .fee-collection-table-wrapper .responsiveTable {
              width: 100%;
              min-width: 1500px;
            }
            .fee-collection-table-wrapper .fee-collection-table th,
            .fee-collection-table-wrapper .fee-collection-table td,
            .fee-collection-table-wrapper .responsiveTable th,
            .fee-collection-table-wrapper .responsiveTable td {
              white-space: nowrap;
            }
            .fee-collection-whatsapp-btn {
              width: 40px;
              height: 40px;
              border-radius: 50%;
              border: none;
              background: #25D366;
              color: #fff;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              box-shadow: 0 2px 8px rgba(37, 211, 102, 0.35);
              transition: transform 0.15s ease, filter 0.15s ease;
            }
            .fee-collection-whatsapp-btn:hover:not(:disabled) {
              filter: brightness(1.05);
              transform: scale(1.05);
            }
            .fee-collection-whatsapp-label {
              display: none;
            }
          }

          .fee-collection-whatsapp-btn:disabled {
            opacity: 0.65;
            cursor: not-allowed;
          }

          .fee-receipt-capture-root {
            position: fixed;
            left: -10000px;
            top: 0;
            z-index: -1;
            pointer-events: none;
          }
        `}
      </style>
      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
        <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
          <div 
            className="d-flex justify-content-between align-items-center"
            style={{
              background: '#F3F4F6',
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '12px',
              paddingBottom: '12px',
              overflow: 'visible',
              borderBottom: '1px solid #D1D3D4',
              width: '100%',
            }}
          >
            {/* Left: Title */}
            <div className="d-flex align-items-center gap-3">
              {/* School Logo */}
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                overflow: 'hidden', 
                background: '#223843',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0,
              }}>
                {!logoError ? (
                  <img 
                    src={logo} 
                    alt="School Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>
              
              <h3 
                className="m-0 fw-bold" 
                style={{ 
                  letterSpacing: '-0.02em',
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(18px, 2.5vw, 22px)',
                  color: '#223843',
                  lineHeight: '1.2',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Fees Collection
              </h3>
            </div>
            
            {/* Right: Profile Icon with Dropdown */}
            {!isMobile && (
              <ProfileDropdown logout={logout} />
            )}
          </div>
        </div>
        </div>
        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          {/* Stat Cards */}
          {/* First Row: Today Collection */}
          <Row className="g-4 mb-4">
            <Col xs={12} sm={6} lg={4}>
              <Card 
                className="h-100"
                style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #D1D3D4',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                <div style={{
                  background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                  padding: '24px 20px',
                  borderRadius: '12px 12px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FaCalendarDay size={24} style={{ color: '#ffffff' }} />
                    </div>
                    <h2 className="mb-0" style={{ 
                      fontSize: '28px', 
                      fontWeight: '700', 
                      color: '#ffffff', 
                      lineHeight: '1',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}>
                      ₹{data.today_collection?.toLocaleString('en-IN') || 0}
                    </h2>
                  </div>
                </div>
                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <p className="mb-0" style={{ 
                    fontSize: '15px', 
                    color: '#111827', 
                    fontWeight: '600', 
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    Today Fee Collection
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={4}>
              <Card 
                className="h-100"
                style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #D1D3D4',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                <div style={{
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  padding: '24px 20px',
                  borderRadius: '12px 12px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FaMoneyBillWave size={24} style={{ color: '#ffffff' }} />
                    </div>
                    <h2 className="mb-0" style={{ 
                      fontSize: '28px', 
                      fontWeight: '700', 
                      color: '#ffffff', 
                      lineHeight: '1',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}>
                      ₹{data.by_cash_today?.toLocaleString('en-IN') || 0}
                    </h2>
                  </div>
                </div>
                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <p className="mb-0" style={{ 
                    fontSize: '15px', 
                    color: '#111827', 
                    fontWeight: '600', 
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    Today By Cash
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={4}>
              <Card 
                className="h-100"
                style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #D1D3D4',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                <div style={{
                  background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
                  padding: '24px 20px',
                  borderRadius: '12px 12px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FaCreditCard size={24} style={{ color: '#ffffff' }} />
                    </div>
                    <h2 className="mb-0" style={{ 
                      fontSize: '28px', 
                      fontWeight: '700', 
                      color: '#ffffff', 
                      lineHeight: '1',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}>
                      ₹{data.by_online_today?.toLocaleString('en-IN') || 0}
                    </h2>
                  </div>
                </div>
                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <p className="mb-0" style={{ 
                    fontSize: '15px', 
                    color: '#111827', 
                    fontWeight: '600', 
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    Today By Online
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Second Row: Monthly Collection */}
          <Row className="g-4 mb-4">
            <Col xs={12} sm={6} lg={4}>
              <Card 
                className="h-100"
                style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #D1D3D4',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                <div style={{
                  background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                  padding: '24px 20px',
                  borderRadius: '12px 12px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FaCalendarAlt size={24} style={{ color: '#ffffff' }} />
                    </div>
                    <h2 className="mb-0" style={{ 
                      fontSize: '28px', 
                      fontWeight: '700', 
                      color: '#ffffff', 
                      lineHeight: '1',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}>
                      ₹{data.monthly_collection?.toLocaleString('en-IN') || 0}
                    </h2>
                  </div>
                </div>
                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <p className="mb-0" style={{ 
                    fontSize: '15px', 
                    color: '#111827', 
                    fontWeight: '600', 
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    Monthly Fee Collection
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={4}>
              <Card 
                className="h-100"
                style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #D1D3D4',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                <div style={{
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  padding: '24px 20px',
                  borderRadius: '12px 12px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FaMoneyBillWave size={24} style={{ color: '#ffffff' }} />
                    </div>
                    <h2 className="mb-0" style={{ 
                      fontSize: '28px', 
                      fontWeight: '700', 
                      color: '#ffffff', 
                      lineHeight: '1',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}>
                      ₹{data.by_cash?.toLocaleString('en-IN') || 0}
                    </h2>
                  </div>
                </div>
                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <p className="mb-0" style={{ 
                    fontSize: '15px', 
                    color: '#111827', 
                    fontWeight: '600', 
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    Monthly By Cash
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} lg={4}>
              <Card 
                className="h-100"
                style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #D1D3D4',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                <div style={{
                  background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
                  padding: '24px 20px',
                  borderRadius: '12px 12px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FaCreditCard size={24} style={{ color: '#ffffff' }} />
                    </div>
                    <h2 className="mb-0" style={{ 
                      fontSize: '28px', 
                      fontWeight: '700', 
                      color: '#ffffff', 
                      lineHeight: '1',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}>
                      ₹{data.by_online?.toLocaleString('en-IN') || 0}
                    </h2>
                  </div>
                </div>
                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <p className="mb-0" style={{ 
                    fontSize: '15px', 
                    color: '#111827', 
                    fontWeight: '600', 
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    Monthly By Online
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Filter Toggle Button */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  padding: '0',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                }}
              >
                <div 
                  style={{
                    background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                    padding: '16px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div 
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <BsCalendar3 size={20} style={{ color: '#ffffff' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <h4 
                      className="m-0 fw-bold" 
                      style={{ 
                        fontSize: '18px', 
                        color: '#ffffff', 
                        fontFamily: 'Inter, system-ui, sans-serif',
                        lineHeight: '1.2',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {title.includes('(') 
                        ? title.split('(')[0].trim() 
                        : title.includes('Collection') 
                          ? 'Collection'
                          : title}
                    </h4>
                    {title.includes('(') ? (
                      <p 
                        className="m-0" 
                        style={{ 
                          fontSize: '13px', 
                          color: 'rgba(255, 255, 255, 0.9)', 
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontWeight: '500',
                          lineHeight: '1.2',
                        }}
                      >
                        {title.match(/\(([^)]+)\)/)?.[1] || ''}
                      </p>
                    ) : title.includes('Collection') && !title.includes('(') ? (
                      <p 
                        className="m-0" 
                        style={{ 
                          fontSize: '13px', 
                          color: 'rgba(255, 255, 255, 0.9)', 
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontWeight: '500',
                          lineHeight: '1.2',
                        }}
                      >
                        {title.split('Collection')[0]?.trim() || ''}
                      </p>
                    ) : null}
                  </div>
                </div>
              </Card>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
              {dataByDate.length > 0 && (
                <button
                  onClick={handleSaveAsPDF}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#EF4444',
                    border: 'none',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#DC2626'
                    e.currentTarget.style.transform = 'scale(1.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#EF4444'
                    e.currentTarget.style.transform = 'scale(1)'
                  }}
                  title="Save as PDF"
                >
                  <BsFileEarmarkPdfFill size={20} />
                </button>
              )}
              {dataByDate.length > 0 && (
                <button
                  onClick={exportToExcel}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                    border: 'none',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#059669'
                    e.currentTarget.style.transform = 'scale(1.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#10B981'
                    e.currentTarget.style.transform = 'scale(1)'
                  }}
                  title="Export to Excel"
                >
                  <BsFileEarmarkSpreadsheetFill size={20} />
                </button>
              )}
            </div>
          </div>

          {/* Modern Filter Section */}
          <div 
            className="filter-card-container"
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: showFilterCard ? 'visible' : 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
              position: 'relative',
              zIndex: showFilterCard ? 10 : 1,
            }}
          >
            {showFilterCard && (
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'visible',
                  position: 'relative',
                }}
              >
                <div 
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Filter by Date & Payment Method
                        </h5>
                        <p className="m-0 mt-1" style={{fontSize: '13px', color: '#6b7280' }}>
                          Select date range and payment method to filter transactions
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff', overflow: 'visible', position: 'relative' }}>
                  <Row className="g-3">
                    <Col xs={12} md={4}>
                      <label className="fw-semibold text-start mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        Filter by Payment Method
                      </label>
                      <Form.Control
                        as="select"
                        value={paymentFilter}
                        onChange={(e) => setPaymentFilter(e.target.value)}
                        style={{
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4',
                          fontSize: '14px',
                          height: '40px',
                          transition: 'all 0.2s ease',
                          backgroundColor: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#223843'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#D1D3D4'
                        }}
                      >
                        <option value="all">All</option>
                        <option value="online">Online</option>
                        <option value="cash">Cash</option>
                        <option value="cheque">Cheque</option>
                      </Form.Control>
                    </Col>
                    <Col xs={12} md={4}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold text-start mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          From Date
                        </label>
                        <div style={{ position: 'relative' }}>
                          <BsCalendar3 
                            style={{ 
                              position: 'absolute', 
                              left: '12px', 
                              top: '50%', 
                              transform: 'translateY(-50%)',
                              color: '#6b7280',
                              fontSize: '16px',
                              zIndex: 1,
                              pointerEvents: 'none'
                            }} 
                          />
                          <Form.Control
                            type="date"
                            value={fromDate}
                            onChange={(e) => handleFromDateChange('from', e.target.value)}
                            style={{
                              paddingLeft: '40px',
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#223843'
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#D1D3D4'
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold text-start mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          To Date
                        </label>
                        <div style={{ position: 'relative' }}>
                          <BsCalendar3 
                            style={{ 
                              position: 'absolute', 
                              left: '12px', 
                              top: '50%', 
                              transform: 'translateY(-50%)',
                              color: '#6b7280',
                              fontSize: '16px',
                              zIndex: 1,
                              pointerEvents: 'none'
                            }} 
                          />
                          <Form.Control
                            type="date"
                            value={toDate}
                            onChange={(e) => handleDateChange('to', e.target.value)}
                            style={{
                              paddingLeft: '40px',
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#223843'
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#D1D3D4'
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>
          {/* Filtered collection totals */}
          {filteredTransactions.length > 0 && (
            <Row className="g-3 mb-4">
              {summaryCards.map((card) => (
                <Col key={card.label} xs={12} sm={6} md={4} lg>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      backgroundColor: '#ffffff',
                      height: '100%',
                    }}
                  >
                    <div
                      style={{
                        background: card.gradient,
                        padding: '14px 16px 10px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: 'rgba(255,255,255,0.9)',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          marginBottom: '4px',
                        }}
                      >
                        {card.label}
                      </div>
                      <div
                        style={{
                          fontSize: '20px',
                          fontWeight: 700,
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          lineHeight: 1.2,
                        }}
                      >
                        {formatINRLocale(card.value)}
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {/* Table */}
          <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
            <div className="fee-collection-table-wrapper">
            <Table className="table mb-0 fee-collection-table" style={{ borderRadius: '12px' }}>
              <Thead>
                <Tr style={{ backgroundColor: '#F3F4F6' }}>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Fees Receipt No.</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Course Name</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Admission Number</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Student Name</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Fees Name</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Transaction Amount</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Discount</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Transaction Date</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Payment Method</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Online</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Cash</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Cheque</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Transaction Number</Th>
                  <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Fees Taken By</Th>
                  <Th className="fee-collection-action-col" style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'center', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredTransactions.length > 0 ? (
                  filteredTransactions.map((item, index) => (
                      <Tr 
                        key={index}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                          transition: 'background-color 0.2s ease',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                        }}
                      >
                        <Td className={isNullOrEmpty(item.id) || isNullOrEmpty(item.transaction_date) ? 'fee-collection-hide-empty-mobile' : ''} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {`${new Date(
                            item.transaction_date,
                          ).getFullYear()}${String(
                            new Date(item.transaction_date).getMonth() + 1,
                          ).padStart(2, '0')}${item.id
                            .toString(16)
                            .toUpperCase()}`}
                        </Td>
                        <Td className={mobileEmptyClass(item.course_name)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.course_name}</Td>
                        <Td className={mobileEmptyClass(item.admission_number)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.admission_number}</Td>
                        <Td className={mobileEmptyClass(item.student_name)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.student_name}</Td>
                        <Td className={mobileEmptyClass(item.fees_name)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.fees_name}</Td>
                        <Td className={mobileEmptyClass(item.transaction_amount)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.transaction_amount ? `₹ ${item.transaction_amount}`:""}</Td>
                        <Td className={mobileEmptyClass(item.discount)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{!isNullOrEmpty(item.discount) ? `₹ ${item.discount}` : ""}</Td>
                        <Td className={mobileEmptyClass(item.transaction_date)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {new Date(item.transaction_date).toLocaleDateString(
                            'en-GB',
                            {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            },
                          )}
                        </Td>
                        <Td className={mobileEmptyClass(item.payment_method)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {item.payment_method?.toUpperCase() || 'Not Selected'}
                        </Td>
                        <Td className={mobileEmptyClass(getOnlineAmount(item))} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {formatINR(getOnlineAmount(item))}
                        </Td>
                        <Td className={mobileEmptyClass(getCashAmount(item))} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {formatINR(getCashAmount(item))}
                        </Td>
                        <Td className={mobileEmptyClass(getChequeAmount(item))} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {formatINR(getChequeAmount(item))}
                        </Td>
                        <Td className={mobileEmptyClass(item.transaction_id)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {item.transaction_id}
                        </Td>
                        <Td className={mobileEmptyClass(item.taken_by_name)} style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.taken_by_name || "-"}</Td>
                        <Td className="fee-collection-action-col" style={{ fontSize: '14px', padding: '16px', border: 'none', textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          <button
                            type="button"
                            className="fee-collection-whatsapp-btn"
                            onClick={() => shareReceiptOnWhatsApp(item)}
                            disabled={sharingFeeId === item.id}
                            title="Send fee receipt image on WhatsApp"
                            aria-label={`Send WhatsApp receipt for ${item.student_name || 'student'}`}
                          >
                            {sharingFeeId === item.id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <FaWhatsapp size={18} />
                            )}
                            <span className="fee-collection-whatsapp-label">
                              Send on WhatsApp
                            </span>
                          </button>
                        </Td>
                      </Tr>
                    ))
                ) : (
                  <Tr>
                    <Td
                      colSpan="15"
                      className="text-center"
                      style={{ padding: '40px', fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {noDataMessage || 'No data found'}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
            </div>
          </Card>
        </div>
      </div>

      {/* Off-screen branded fee receipt for image capture */}
      <div className="fee-receipt-capture-root" aria-hidden="true">
        {receiptItem && (
          <div
            ref={receiptCardRef}
            style={{
              width: 400,
              background: 'linear-gradient(165deg, #1a2c36 0%, #223843 42%, #1e3a45 100%)',
              padding: '26px 20px 32px',
              fontFamily:
                'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Soft atmospheric accents */}
            <div
              style={{
                position: 'absolute',
                width: 180,
                height: 180,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(16,185,129,0.22) 0%, transparent 70%)',
                top: -50,
                right: -40,
              }}
            />
            <div
              style={{
                position: 'absolute',
                width: 140,
                height: 140,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(245,158,11,0.16) 0%, transparent 70%)',
                bottom: 40,
                left: -30,
              }}
            />

            {/* Brand header */}
            <div style={{ textAlign: 'center', color: '#ffffff', marginBottom: 18, position: 'relative' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 999,
                  padding: '5px 12px',
                  marginBottom: 14,
                  fontSize: 11,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.85)',
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: '#10B981',
                    display: 'inline-block',
                  }}
                />
                Secure fee receipt
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={instituteDisplayLogo}
                    alt=""
                    crossOrigin="anonymous"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                    onError={(e) => {
                      e.currentTarget.onerror = null
                      e.currentTarget.src = NO_IMAGE_SRC
                    }}
                  />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.2,
                      maxWidth: 240,
                    }}
                  >
                    {(instituteDisplayName || 'Institute').slice(0, 28)}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.72)', marginTop: 2 }}>
                    Official payment confirmation
                  </div>
                </div>
              </div>
            </div>

            {/* Ticket card */}
            <div
              style={{
                position: 'relative',
                background: '#ffffff',
                borderRadius: 16,
                overflow: 'hidden',
                boxShadow: '0 14px 36px rgba(0,0,0,0.28)',
              }}
            >
              {/* Top accent bar */}
              <div
                style={{
                  height: 5,
                  background: 'linear-gradient(90deg, #223843 0%, #10B981 55%, #F59E0B 100%)',
                }}
              />

              <div style={{ padding: '22px 20px 18px', textAlign: 'center' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: '#ECFDF5',
                    color: '#059669',
                    borderRadius: 999,
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 12,
                  }}
                >
                  <FaCheck size={10} />
                  Payment successful
                </div>

                <div
                  style={{
                    color: '#6B7280',
                    fontSize: 12,
                    marginBottom: 4,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    fontWeight: 600,
                  }}
                >
                  Paid to
                </div>
                <div
                  style={{
                    color: '#223843',
                    fontSize: 20,
                    fontWeight: 700,
                    lineHeight: 1.25,
                    marginBottom: 4,
                  }}
                >
                  {name || 'Institute'}
                </div>
                <div
                  style={{
                    color: '#6B7280',
                    fontSize: 13,
                    marginBottom: 18,
                    wordBreak: 'break-word',
                    lineHeight: 1.4,
                  }}
                >
                  {receiptItem.fees_name || 'Fee payment'}
                  {receiptItem.course_name ? (
                    <span style={{ color: '#9CA3AF' }}>
                      {' '}
                      · {receiptItem.course_name}
                    </span>
                  ) : null}
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      fontSize: 44,
                      fontWeight: 800,
                      color: '#223843',
                      lineHeight: 1,
                      letterSpacing: '-0.03em',
                    }}
                  >
                    ₹{receiptItem.transaction_amount ?? '0'}
                  </div>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'linear-gradient(145deg, #10B981 0%, #059669 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(16,185,129,0.4)',
                    }}
                  >
                    <FaCheck size={15} />
                  </div>
                </div>

                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    border: '1px solid #D1D3D4',
                    background: '#F3F4F6',
                    borderRadius: 999,
                    padding: '6px 14px',
                    fontSize: 12,
                    color: '#223843',
                    fontWeight: 600,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background:
                        (receiptItem.payment_method || '').toLowerCase() === 'cash'
                          ? '#F59E0B'
                          : '#10B981',
                      display: 'inline-block',
                    }}
                  />
                  {(receiptItem.payment_method || 'FEE').toUpperCase()} PAYMENT
                </div>
              </div>

              {/* Ticket notch + dashed divider */}
              <div style={{ position: 'relative', height: 22 }}>
                <div
                  style={{
                    position: 'absolute',
                    left: -11,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#223843',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    right: -11,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#223843',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: 20,
                    right: 20,
                    top: '50%',
                    borderTop: '1.5px dashed #D1D3D4',
                  }}
                />
              </div>

              <div style={{ padding: '6px 18px 20px' }}>
                <div
                  style={{
                    textAlign: 'center',
                    color: '#6B7280',
                    fontSize: 12,
                    marginBottom: 12,
                    fontWeight: 500,
                  }}
                >
                  {receiptItem.transaction_date
                    ? moment(receiptItem.transaction_date).format(
                        'D MMM YYYY, h:mm A',
                      )
                    : ''}
                </div>

                <div
                  style={{
                    background: '#F3F4F6',
                    borderRadius: 12,
                    border: '1px solid #E5E7EB',
                    padding: '12px 14px',
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    Paid by
                  </div>
                  <div
                    style={{
                      color: '#223843',
                      fontSize: 16,
                      fontWeight: 700,
                      marginBottom: 2,
                    }}
                  >
                    {receiptItem.student_name || 'Student'}
                  </div>
                  <div style={{ color: '#6B7280', fontSize: 12 }}>
                    Admission No. {receiptItem.admission_number || '-'}
                  </div>
                </div>

                {/* Detail rows */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  {[
                    {
                      label: 'Receipt No',
                      value: getReceiptNo(receiptItem),
                    },
                    receiptItem.transaction_id
                      ? {
                          label: 'Transaction ID',
                          value: String(receiptItem.transaction_id),
                        }
                      : null,
                    !isNullOrEmpty(receiptItem.discount)
                      ? {
                          label: 'Discount',
                          value: `₹ ${receiptItem.discount}`,
                        }
                      : null,
                    {
                      label: 'Collected by',
                      value: receiptItem.taken_by_name || '-',
                    },
                  ]
                    .filter(Boolean)
                    .map((row) => (
                      <div
                        key={row.label}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 12,
                          fontSize: 12,
                        }}
                      >
                        <span style={{ color: '#9CA3AF', flexShrink: 0 }}>{row.label}</span>
                        <span
                          style={{
                            color: '#223843',
                            fontWeight: 600,
                            textAlign: 'right',
                            wordBreak: 'break-all',
                          }}
                        >
                          {row.value}
                        </span>
                      </div>
                    ))}
                </div>

                <div
                  style={{
                    textAlign: 'center',
                    borderTop: '1px solid #E5E7EB',
                    paddingTop: 10,
                    fontSize: 11,
                    color: '#9CA3AF',
                    letterSpacing: '0.02em',
                  }}
                >
                  Thank you for your payment · {(name || 'Institute').slice(0, 24)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
